import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  HardHat, LogOut, Wallet, FileText, MessageSquare, User,
  Bell, ChevronRight, Clock, DollarSign, CheckCircle, XCircle,
  AlertCircle, Plus, Shield, BadgeCheck
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboard();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/contractors/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 403) {
        navigate('/contractor/setup');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      await axios.get(`${API}/wallet/checkout-status/${sessionId}`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to check payment:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const { profile, wallet, pending_leads, proposals, won_jobs, contact_requests, stats } = dashboardData || {};

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            
            <div className="flex items-center gap-3 md:gap-4">
              <Link to="/tips" className="text-slate-300 hover:text-white text-sm hidden md:flex items-center gap-1.5" data-testid="dashboard-tips-link">
                {t('nav.tips')}
              </Link>
              <LanguageSwitcher variant="dark" />
              <button className="text-slate-400 hover:text-white relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {pending_leads?.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-600 rounded-full text-xs text-white flex items-center justify-center">
                    {pending_leads.length}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-300" />
                </div>
                <span className="text-white text-sm hidden md:block">{user?.first_name}</span>
              </div>
              <button 
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Profile Setup Alert */}
        {!profile && (
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="text-orange-800">Complete your profile to start receiving leads</span>
            </div>
            <Link 
              to="/contractor/setup"
              className="bg-orange-600 text-white px-4 py-2 rounded-sm text-sm font-bold"
              data-testid="setup-profile-btn"
            >
              Setup Profile
            </Link>
          </div>
        )}

        {/* Wallet Balance Warning */}
        {wallet && wallet.balance < wallet.minimum_balance && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">Your wallet balance is below $300. Top up to receive leads.</span>
            </div>
            <Link 
              to="/contractor/wallet"
              className="bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-bold"
              data-testid="topup-wallet-btn"
            >
              Top Up Wallet
            </Link>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">Wallet Balance</span>
              <Wallet className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="wallet-balance">
              ${wallet?.balance?.toFixed(2) || '0.00'}
            </p>
            <Link to="/contractor/wallet" className="text-orange-600 text-sm font-medium mt-2 inline-block">
              Manage Wallet
            </Link>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">Pending Leads</span>
              <FileText className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="pending-leads-count">
              {pending_leads?.length || 0}
            </p>
            <span className="text-slate-500 text-sm">Awaiting your proposal</span>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">Won Jobs</span>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="won-jobs-count">
              {stats?.total_wins || 0}
            </p>
            <span className="text-slate-500 text-sm">Total jobs won</span>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">Quality Score</span>
              <BadgeCheck className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="quality-score">
              {stats?.quality_score?.toFixed(0) || 0}
            </p>
            <span className="text-slate-500 text-sm">Out of 100</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['overview', 'leads', 'proposals', 'messages'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-sm font-bold text-sm uppercase whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pending Leads */}
            {(activeTab === 'overview' || activeTab === 'leads') && (
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-bold text-slate-900">Pending Leads</h3>
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-sm font-bold">
                    {pending_leads?.length || 0} new
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {pending_leads?.length > 0 ? (
                    pending_leads.map((lead) => (
                      <div key={lead.id} className="p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-bold text-slate-900">{lead.job?.title}</h4>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{lead.job?.description}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                24h to respond
                              </span>
                              <span className="text-xs font-bold text-orange-600">
                                ${lead.job?.lead_price} if selected
                              </span>
                            </div>
                          </div>
                          <Link
                            to={`/contractor/lead/${lead.id}`}
                            className="bg-orange-600 text-white px-4 py-2 rounded-sm text-sm font-bold flex items-center gap-1"
                            data-testid={`submit-proposal-${lead.id}`}
                          >
                            Submit Proposal <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No pending leads</p>
                      <p className="text-sm text-slate-400 mt-1">New leads will appear here when matched</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Active Proposals */}
            {(activeTab === 'overview' || activeTab === 'proposals') && (
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-bold text-slate-900">My Proposals</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {proposals?.length > 0 ? (
                    proposals.map((proposal) => (
                      <div key={proposal.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-bold text-slate-900">{proposal.job?.title}</h4>
                            <p className="text-sm text-slate-500">
                              ${proposal.estimated_price_min} - ${proposal.estimated_price_max}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            proposal.status === 'selected' ? 'bg-green-100 text-green-700' :
                            proposal.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {proposal.status}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No proposals yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Won Jobs */}
            {activeTab === 'overview' && won_jobs?.length > 0 && (
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-bold text-slate-900">Won Jobs</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {won_jobs.map((job) => (
                    <div key={job.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-slate-900">{job.title}</h4>
                          <p className="text-sm text-slate-500">{job.zip_code}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/contractor/messages/${job.id}`}
                            className="bg-blue-600 text-white px-3 py-1 rounded-sm text-sm font-bold flex items-center gap-1"
                            data-testid={`message-client-${job.id}`}
                          >
                            <MessageSquare className="w-4 h-4" /> Message
                          </Link>
                          {job.contact_revealed ? (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">
                              Contact Revealed
                            </span>
                          ) : (
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold">
                              Pending Reveal
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                  {profile?.profile_photo_url ? (
                    <img src={profile.profile_photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{profile?.business_name || 'Setup Profile'}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {profile?.license_verified === 'approved' && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <Shield className="w-3 h-3" /> Licensed
                      </span>
                    )}
                    {profile?.insurance_verified === 'approved' && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <Shield className="w-3 h-3" /> Insured
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Link
                to="/contractor/profile"
                className="block w-full text-center bg-slate-100 text-slate-700 py-2 rounded-sm text-sm font-bold hover:bg-slate-200 transition-colors"
                data-testid="edit-profile-btn"
              >
                Edit Profile
              </Link>
            </div>

            {/* Pending Contact Requests */}
            {contact_requests?.length > 0 && (
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-bold text-slate-900">Pending Contact Requests</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {contact_requests.map((request) => (
                    <div key={request.id} className="p-4">
                      <p className="text-sm text-slate-600">Waiting for client approval</p>
                      <span className="text-xs text-orange-600 font-bold">${request.charge_amount} on approval</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-sm border border-slate-200 p-4">
              <h3 className="font-bold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/contractor/wallet"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Wallet className="w-4 h-4" /> Top Up Wallet
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
                <Link
                  to="/contractor/profile"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <User className="w-4 h-4" /> Update Profile
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorDashboard;
